import React, { useEffect, useMemo, useState } from "react";
import { FaPlus, FaSyncAlt, FaQrcode, FaEdit, FaToggleOn, FaToggleOff, FaTrashAlt } from "react-icons/fa";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import { PageLayout } from "../../components/layout";
import { Table, TableColumn } from "../../components/Table";
import PromotionFormModal from "../../components/Promotion/PromotionFormModal";
import QRPreviewModal from "../../components/Promotion/QRPreviewModal";
import { showErrorToast, showSuccessToast } from "../../lib/ToastUtility";
import config from "../../config";
import {
  activatePromotion,
  createPromotion,
  deactivatePromotion,
  deletePromotion,
  getPromotions,
  updatePromotion,
} from "../../services/PromotionService";
import {
  Promotion,
  PromotionFormValues,
  PROMOTION_STATUS,
} from "../../types/promotion.type";

const buildQrUrl = (trackingId: string) => {
  const base = (config.apiBaseUrl || "").replace(/\/$/, "");
  return `${base}/qr/${encodeURIComponent(trackingId)}`;
};

const PromotionsPage: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");

  const [search, setSearch] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Promotion | null>(null);
  const [qrTarget, setQrTarget] = useState<Promotion | null>(null);

  const fetchPromotions = async () => {
    setLoading(true);
    setListError("");
    try {
      const data = await getPromotions();
      // Hide soft-deleted just in case the API ever returns them.
      setPromotions(data.filter((p) => p.status !== PROMOTION_STATUS.DELETED));
    } catch (error) {
      const message = (error as Error).message || "Failed to load promotions";
      setListError(message);
      showErrorToast(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  const filteredPromotions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return promotions;
    return promotions.filter(
      (p) =>
        p.campaignName?.toLowerCase().includes(term) ||
        p.trackingId?.toLowerCase().includes(term),
    );
  }, [promotions, search]);

  const handleCreate = () => {
    setEditingPromotion(null);
    setFormError("");
    setShowFormModal(true);
  };

  const handleEdit = (promotion: Promotion) => {
    setEditingPromotion(promotion);
    setFormError("");
    setShowFormModal(true);
  };

  const handleSubmitForm = async (values: PromotionFormValues) => {
    setFormSubmitting(true);
    setFormError("");
    try {
      if (editingPromotion) {
        await updatePromotion({
          ...editingPromotion,
          campaignName: values.campaignName,
          trackingId: values.trackingId,
          totalDonations: values.totalDonations,
          donationsPerUser: values.donationsPerUser,
          foodBarsPerDonation: values.foodBarsPerDonation,
          isMetered: values.isMetered,
        });
        showSuccessToast("Promotion updated successfully");
      } else {
        await createPromotion({
          campaignName: values.campaignName,
          trackingId: values.trackingId,
          totalDonations: values.totalDonations,
          donationsPerUser: values.donationsPerUser,
          foodBarsPerDonation: values.foodBarsPerDonation,
          isMetered: values.isMetered,
          status: PROMOTION_STATUS.ACTIVE,
          donationCount: 0,
        });
        showSuccessToast("Promotion created successfully");
      }
      setShowFormModal(false);
      setEditingPromotion(null);
      await fetchPromotions();
    } catch (error) {
      const message = (error as Error).message || "Failed to save promotion";
      setFormError(message);
      showErrorToast(message);
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleToggleStatus = async (promotion: Promotion) => {
    const key = `status-${promotion.id}`;
    setActionLoadingKey(key);
    try {
      if (promotion.status === PROMOTION_STATUS.ACTIVE) {
        await deactivatePromotion(promotion.id);
        showSuccessToast("Promotion deactivated");
      } else {
        await activatePromotion(promotion.id);
        showSuccessToast("Promotion activated");
      }
      await fetchPromotions();
    } catch (error) {
      showErrorToast(
        (error as Error).message || "Failed to update promotion status",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const key = `delete-${deleteTarget.id}`;
    setActionLoadingKey(key);
    try {
      await deletePromotion(deleteTarget.id);
      showSuccessToast("Promotion deleted");
      setDeleteTarget(null);
      await fetchPromotions();
    } catch (error) {
      showErrorToast((error as Error).message || "Failed to delete promotion");
    } finally {
      setActionLoadingKey(null);
    }
  };

  const isQrCampaign = (promotion: Promotion) =>
    promotion.isMetered || promotion.trackingId?.startsWith("qr_");

  const columns: TableColumn<Promotion>[] = [
    {
      id: "campaignName",
      label: "Promotion Name",
      width: "22%",
      priority: "high",
    },
    {
      id: "trackingId",
      label: "Tracking ID / Redeem Code",
      width: "20%",
      priority: "high",
      format: (value) => (
        <span className="font-mono text-xs text-gray-700 dark:text-gray-200">
          {String(value || "-")}
        </span>
      ),
    },
    {
      id: "type",
      label: "Type",
      width: "10%",
      priority: "medium",
      format: (_, row) => (
        <Badge
          label={isQrCampaign(row) ? "QR" : "Redeem"}
          variant={isQrCampaign(row) ? "primary" : "info"}
          size="md"
        />
      ),
    },
    {
      id: "totalDonations",
      label: "Allowed",
      width: "10%",
      priority: "medium",
      format: (value) => Number(value || 0).toLocaleString(),
    },
    {
      id: "donationCount",
      label: "Used",
      width: "8%",
      priority: "low",
      format: (value) => Number(value || 0).toLocaleString(),
    },
    {
      id: "status",
      label: "Status",
      width: "10%",
      priority: "medium",
      format: (value) => (
        <Badge
          label={value === PROMOTION_STATUS.ACTIVE ? "Active" : "Inactive"}
          variant={value === PROMOTION_STATUS.ACTIVE ? "success" : "default"}
          size="md"
        />
      ),
    },
    {
      id: "actions",
      label: "Actions",
      width: "14%",
      priority: "high",
      type: "actions",
      format: (_, row) => (
        <div
          className="flex flex-wrap items-center gap-2"
          onClick={(event) => event.stopPropagation()}
        >
          {isQrCampaign(row) && (
            <Button
              variant="tertiary"
              size="sm"
              startIcon={<FaQrcode />}
              aria-label="Preview QR code"
              title="Preview QR code"
              className="h-9 w-9 px-0 rounded-xl"
              onClick={() => setQrTarget(row)}
            >
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            startIcon={<FaEdit />}
            aria-label="Edit promotion"
            title="Edit promotion"
            className="h-9 w-9 px-0 rounded-xl"
            onClick={() => handleEdit(row)}
            disabled={
              actionLoadingKey !== null && !actionLoadingKey.endsWith(`-${row.id}`)
            }
          >
          </Button>
          <Button
            variant="secondary"
            size="sm"
            startIcon={row.status === PROMOTION_STATUS.ACTIVE ? <FaToggleOff /> : <FaToggleOn />}
            aria-label={row.status === PROMOTION_STATUS.ACTIVE ? "Deactivate promotion" : "Activate promotion"}
            title={row.status === PROMOTION_STATUS.ACTIVE ? "Deactivate promotion" : "Activate promotion"}
            className="h-9 w-9 px-0 rounded-xl"
            loading={actionLoadingKey === `status-${row.id}`}
            disabled={
              actionLoadingKey !== null && actionLoadingKey !== `status-${row.id}`
            }
            onClick={() => handleToggleStatus(row)}
          >
          </Button>
          <Button
            variant="tertiary"
            size="sm"
            startIcon={<FaTrashAlt />}
            aria-label="Delete promotion"
            title="Delete promotion"
            className="h-9 w-9 px-0 rounded-xl text-red-600 dark:text-red-300"
            loading={actionLoadingKey === `delete-${row.id}`}
            disabled={
              actionLoadingKey !== null && actionLoadingKey !== `delete-${row.id}`
            }
            onClick={() => setDeleteTarget(row)}
          >
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageLayout
        title="Promotions"
        subtitle="Create and manage QR campaigns and redeem codes."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              startIcon={<FaSyncAlt />}
              onClick={fetchPromotions}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              variant="primary"
              size="sm"
              startIcon={<FaPlus />}
              onClick={handleCreate}
            >
              Add Promotion
            </Button>
          </div>
        }
      >
        {listError && (
          <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
            {listError}
          </div>
        )}

        <div className="p-4">
          <div className="mb-4">
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by name or tracking ID..."
              className="w-full rounded-md border border-gray-300 p-2 text-sm focus:border-primary-100 focus:ring-primary-100 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100"
            />
          </div>

          <div className="overflow-x-auto">
            <Table
              data={filteredPromotions}
              columns={columns}
              loading={loading}
              hoverable={false}
              emptyMessage="No promotions yet. Click Add Promotion to create one."
              rowKey={(row) => String(row.id)}
            />
          </div>
        </div>
      </PageLayout>

      <PromotionFormModal
        open={showFormModal}
        loading={formSubmitting}
        error={formError}
        editingPromotion={editingPromotion}
        onClose={() => {
          if (!formSubmitting) {
            setShowFormModal(false);
            setEditingPromotion(null);
            setFormError("");
          }
        }}
        onConfirm={handleSubmitForm}
      />

      <QRPreviewModal
        open={Boolean(qrTarget)}
        promotionName={qrTarget?.campaignName || ""}
        trackingId={qrTarget?.trackingId || ""}
        qrUrl={qrTarget ? buildQrUrl(qrTarget.trackingId) : ""}
        onClose={() => setQrTarget(null)}
      />

      <ConfirmModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Promotion"
        message={`Are you sure you want to delete "${deleteTarget?.campaignName || ""}"? This action cannot be undone from the UI.`}
        confirmLabel="Delete"
        confirmVariant="primary"
      />
    </>
  );
};

export default PromotionsPage;
